import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import {
  LayoutDashboard, Calendar, LogOut, Clock, ArrowLeft,
  Package, Truck, MapPin, User, AlertTriangle, Search, Users
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ManualSlotPicker } from "@/components/admin/ManualSlotPicker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminNewBooking = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  
  // Customer search
  const [customerSearch, setCustomerSearch] = useState("");
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerResults, setShowCustomerResults] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  
  // Booking type
  const [bookingType, setBookingType] = useState("both"); // "pickup", "delivery", "both"
  
  // Zone selection
  const [selectedZone, setSelectedZone] = useState("");
  
  // Pickup state
  const [pickupDates, setPickupDates] = useState([]);
  const [pickupDate, setPickupDate] = useState(null);
  const [pickupSlots, setPickupSlots] = useState([]);
  const [selectedPickupSlot, setSelectedPickupSlot] = useState(null);
  
  // Delivery state
  const [deliveryDate, setDeliveryDate] = useState(null);
  const [deliverySlots, setDeliverySlots] = useState([]);
  const [selectedDeliverySlot, setSelectedDeliverySlot] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    address: "",
    phone: "",
    email: "",
    remarks: "",
  });

  const getAuth = useCallback(() => {
    const auth = localStorage.getItem("adminAuth");
    if (!auth) {
      navigate("/admin");
      return null;
    }
    return { Authorization: `Basic ${auth}` };
  }, [navigate]);

  // Load prefilled customer from sessionStorage (from Clients page)
  useEffect(() => {
    const prefilledCustomer = sessionStorage.getItem("prefilledCustomer");
    if (prefilledCustomer) {
      try {
        const customer = JSON.parse(prefilledCustomer);
        setSelectedCustomer(customer);
        setFormData({
          firstName: customer.first_name || "",
          lastName: customer.last_name || "",
          address: customer.address || "",
          phone: customer.phone || "",
          email: customer.email || "",
          remarks: "",
        });
        // Clear after loading
        sessionStorage.removeItem("prefilledCustomer");
      } catch (e) {
        console.error("Error parsing prefilled customer:", e);
      }
    }
  }, []);

  // Search customers
  const searchCustomers = async (query) => {
    if (!query || query.length < 2) {
      setCustomerResults([]);
      setShowCustomerResults(false);
      return;
    }
    
    const headers = getAuth();
    if (!headers) return;
    
    try {
      const response = await axios.get(`${API}/admin/customers`, { 
        headers, 
        params: { search: query } 
      });
      setCustomerResults(response.data);
      setShowCustomerResults(true);
    } catch (error) {
      console.error("Error searching customers:", error);
    }
  };

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchCustomers(customerSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [customerSearch]);

  // Select a customer
  const selectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setFormData({
      firstName: customer.first_name || "",
      lastName: customer.last_name || "",
      address: customer.address || "",
      phone: customer.phone || "",
      email: customer.email || "",
      remarks: "",
    });
    setShowCustomerResults(false);
    setCustomerSearch("");
    toast.success(`Client ${customer.first_name} ${customer.last_name} sélectionné`);
  };

  // Clear selected customer
  const clearCustomer = () => {
    setSelectedCustomer(null);
    setFormData({
      firstName: "",
      lastName: "",
      address: "",
      phone: "",
      email: "",
      remarks: "",
    });
  };

  // Fetch available dates when zone changes
  useEffect(() => {
    if (!selectedZone) return;
    
    const fetchDates = async () => {
      try {
        const response = await axios.get(`${API}/slots/${selectedZone}/dates`);
        setPickupDates(response.data.map(d => new Date(d)));
        // Reset selections
        setPickupDate(null);
        setSelectedPickupSlot(null);
        setDeliveryDate(null);
        setSelectedDeliverySlot(null);
      } catch (error) {
        console.error("Error fetching dates:", error);
      }
    };
    fetchDates();
  }, [selectedZone]);

  // Fetch pickup slots when pickup date changes
  useEffect(() => {
    if (!pickupDate || !selectedZone) return;
    
    const fetchSlots = async () => {
      try {
        const dateStr = format(pickupDate, "yyyy-MM-dd");
        const response = await axios.get(`${API}/slots/${selectedZone}?date=${dateStr}`);
        setPickupSlots(response.data);
      } catch (error) {
        toast.error("Erreur lors du chargement des créneaux");
      }
    };
    fetchSlots();
  }, [pickupDate, selectedZone]);

  // Fetch delivery slots when delivery date changes
  useEffect(() => {
    if (!deliveryDate || !selectedZone) return;
    
    const fetchSlots = async () => {
      try {
        const dateStr = format(deliveryDate, "yyyy-MM-dd");
        const response = await axios.get(`${API}/slots/${selectedZone}?date=${dateStr}`);
        setDeliverySlots(response.data);
      } catch (error) {
        toast.error("Erreur lors du chargement des créneaux");
      }
    };
    fetchSlots();
  }, [deliveryDate, selectedZone]);

  const isDateAvailable = (date) => {
    return pickupDates.some(d => 
      d.getFullYear() === date.getFullYear() &&
      d.getMonth() === date.getMonth() &&
      d.getDate() === date.getDate()
    );
  };

  const isDeliveryDateValid = (date) => {
    if (!pickupDate) return false;
    return date >= pickupDate && isDateAvailable(date);
  };

  const handlePickupDateSelect = (date) => {
    setPickupDate(date);
    setSelectedPickupSlot(null);
    if (bookingType === "both") {
      setDeliveryDate(null);
      setSelectedDeliverySlot(null);
    }
  };

  const handleDeliveryDateSelect = (date) => {
    setDeliveryDate(date);
    setSelectedDeliverySlot(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Calculate surcharge based on days difference
  // 24h (1 day) = +50%, 48h (2 days) = +30%, 3+ days = 0%
  const calculateSurcharge = () => {
    if (bookingType !== "both" || !pickupDate || !deliveryDate) {
      return { hasSurcharge: false, surchargePercent: 0 };
    }
    const daysDiff = Math.floor((deliveryDate - pickupDate) / (1000 * 60 * 60 * 24));
    if (daysDiff <= 1) {
      return { hasSurcharge: true, surchargePercent: 50 };
    } else if (daysDiff <= 2) {
      return { hasSurcharge: true, surchargePercent: 30 };
    }
    return { hasSurcharge: false, surchargePercent: 0 };
  };
  
  const { hasSurcharge, surchargePercent } = calculateSurcharge();

  // Check if can show client form
  const canShowClientForm = () => {
    if (bookingType === "pickup") return selectedPickupSlot;
    if (bookingType === "delivery") return selectedDeliverySlot;
    return selectedPickupSlot && selectedDeliverySlot;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const headers = getAuth();
    if (!headers) return;
    
    // Validation based on booking type
    if (bookingType === "pickup" && !selectedPickupSlot) {
      toast.error("Veuillez sélectionner un créneau de ramassage");
      return;
    }
    if (bookingType === "delivery" && !selectedDeliverySlot) {
      toast.error("Veuillez sélectionner un créneau de livraison");
      return;
    }
    if (bookingType === "both" && (!selectedPickupSlot || !selectedDeliverySlot)) {
      toast.error("Veuillez sélectionner les créneaux de ramassage et livraison");
      return;
    }
    
    if (!formData.firstName || !formData.lastName || !formData.address) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/admin/bookings/manual`, {
        zone_id: selectedZone,
        booking_type: bookingType,
        pickup_slot_id: selectedPickupSlot?.id || null,
        delivery_slot_id: selectedDeliverySlot?.id || null,
        first_name: formData.firstName,
        last_name: formData.lastName,
        address: formData.address,
        phone: formData.phone || null,
        email: formData.email || null,
        remarks: formData.remarks || null,
      }, { headers });
      
      toast.success("Réservation créée avec succès !");
      navigate("/admin/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la création");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("adminAuth");
    navigate("/admin");
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Sidebar */}
      <aside className="admin-sidebar hidden md:block">
        <div className="flex items-center justify-center mb-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_pressingexpress/artifacts/d4qg5zqd_Capture%20d%E2%80%99%C3%A9cran%202025-01-06%20102414.png" 
            alt="Pressing Sakura" 
            className="h-16 w-auto bg-white rounded-lg p-1"
          />
        </div>

        <nav className="space-y-2">
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/dashboard")}
            data-testid="nav-dashboard"
          >
            <LayoutDashboard className="w-5 h-5" />
            <span>Tableau de bord</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/planning")}
            data-testid="nav-planning"
          >
            <Calendar className="w-5 h-5" />
            <span>Planning</span>
          </div>
          <div
            className="admin-nav-item"
            onClick={() => navigate("/admin/slots")}
            data-testid="nav-slots"
          >
            <Clock className="w-5 h-5" />
            <span>Gérer les créneaux</span>
          </div>
        </nav>

        <div className="absolute bottom-6 left-6 right-6">
          <button
            onClick={handleLogout}
            className="admin-nav-item w-full text-slate-400 hover:text-white"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-6 md:p-10 overflow-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate("/admin/dashboard")}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Nouvelle réservation
          </h1>
        </div>

        {/* Pre-selected Customer Banner */}
        {selectedCustomer && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <Users className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="font-semibold text-green-800">
                  Client sélectionné : {selectedCustomer.first_name} {selectedCustomer.last_name}
                </p>
                <p className="text-sm text-green-600">
                  {selectedCustomer.address}
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={clearCustomer}
              className="border-green-300 text-green-700 hover:bg-green-100"
            >
              Changer
            </Button>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Form */}
          <div className="lg:col-span-2 space-y-6">
            {/* Zone Selection */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <MapPin className="w-5 h-5 text-primary" />
                  Zone de service
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <Select value={selectedZone} onValueChange={setSelectedZone}>
                  <SelectTrigger data-testid="zone-select">
                    <SelectValue placeholder="Sélectionner une zone" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="lyon">Lyon</SelectItem>
                    <SelectItem value="saint-rambert">Saint-Rambert-d'Albon</SelectItem>
                    <SelectItem value="francheville">Francheville</SelectItem>
                  </SelectContent>
                </Select>
                
                {selectedZone && (
                  <div className="pt-2 border-t">
                    <Label className="text-sm font-medium mb-3 block">Type de réservation</Label>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant={bookingType === "both" ? "default" : "outline"}
                        size="sm"
                        onClick={() => setBookingType("both")}
                        className={bookingType === "both" ? "btn-primary" : ""}
                      >
                        <Package className="w-4 h-4 mr-1" />
                        <Truck className="w-4 h-4 mr-2" />
                        Ramassage + Livraison
                      </Button>
                      <Button
                        type="button"
                        variant={bookingType === "pickup" ? "default" : "outline"}
                        size="sm"
                        onClick={() => { setBookingType("pickup"); setSelectedDeliverySlot(null); }}
                        className={bookingType === "pickup" ? "bg-pink-500 hover:bg-pink-600" : ""}
                      >
                        <Package className="w-4 h-4 mr-2" />
                        Ramassage seul
                      </Button>
                      <Button
                        type="button"
                        variant={bookingType === "delivery" ? "default" : "outline"}
                        size="sm"
                        onClick={() => { setBookingType("delivery"); setSelectedPickupSlot(null); }}
                        className={bookingType === "delivery" ? "bg-green-500 hover:bg-green-600" : ""}
                      >
                        <Truck className="w-4 h-4 mr-2" />
                        Livraison seule
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pickup */}
            {selectedZone && (bookingType === "both" || bookingType === "pickup") && (
              <Card className="fade-in">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Package className="w-5 h-5 text-pink-500" />
                    Ramassage
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col md:flex-row gap-4">
                    <CalendarComponent
                      mode="single"
                      selected={pickupDate}
                      onSelect={handlePickupDateSelect}
                      locale={fr}
                      disabled={(date) => !isDateAvailable(date)}
                      className="rounded-xl border-0"
                    />
                    {pickupDate && (
                      <div className="flex-1">
                        <p className="text-sm font-medium mb-3">Créneaux disponibles :</p>
                        {pickupSlots.length === 0 ? (
                          <p className="text-slate-500 text-sm">Aucun créneau</p>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {pickupSlots.map((slot) => {
                              const isSelected = selectedPickupSlot?.id === slot.id;
                              return (
                                <button
                                  key={slot.id}
                                  onClick={() => setSelectedPickupSlot(slot)}
                                  className={`p-3 rounded-lg border text-left transition-all ${
                                    isSelected 
                                      ? "border-pink-500 bg-pink-50 ring-2 ring-pink-200" 
                                      : "border-slate-200 hover:border-pink-300"
                                  }`}
                                >
                                  <p className="font-medium text-sm">{slot.start_time} - {slot.end_time}</p>
                                  <p className="text-xs text-slate-500">
                                    {slot.max_bookings - slot.current_bookings} place(s)
                                  </p>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <ManualSlotPicker
                    zoneId={selectedZone}
                    getAuth={getAuth}
                    prefix="pickup"
                    onCreated={(slot, date) => {
                      setPickupDate(date);
                      setSelectedPickupSlot(slot);
                      if (bookingType === "both") { setDeliveryDate(null); setSelectedDeliverySlot(null); }
                    }}
                  />
                </CardContent>
              </Card>
            )}

            {/* Delivery */}
            {selectedZone && (
              (bookingType === "both" && selectedPickupSlot) || 
              bookingType === "delivery"
            ) && (
              <Card className="fade-in">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Truck className="w-5 h-5 text-green-500" />
                    Livraison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col md:flex-row gap-4">
                    <CalendarComponent
                      mode="single"
                      selected={deliveryDate}
                      onSelect={handleDeliveryDateSelect}
                      locale={fr}
                      disabled={(date) => bookingType === "delivery" ? !isDateAvailable(date) : !isDeliveryDateValid(date)}
                      className="rounded-xl border-0"
                    />
                    {deliveryDate && (
                      <div className="flex-1">
                        <p className="text-sm font-medium mb-3">Créneaux disponibles :</p>
                        {deliverySlots.length === 0 ? (
                          <p className="text-slate-500 text-sm">Aucun créneau</p>
                        ) : (
                          <div className="grid grid-cols-2 gap-2">
                            {deliverySlots.map((slot) => {
                              const isSelected = selectedDeliverySlot?.id === slot.id;
                              return (
                                <button
                                  key={slot.id}
                                  onClick={() => setSelectedDeliverySlot(slot)}
                                  className={`p-3 rounded-lg border text-left transition-all ${
                                    isSelected 
                                      ? "border-green-500 bg-green-50 ring-2 ring-green-200" 
                                      : "border-slate-200 hover:border-green-300"
                                  }`}
                                >
                                  <p className="font-medium text-sm">{slot.start_time} - {slot.end_time}</p>
                                  <p className="text-xs text-slate-500">
                                    {slot.max_bookings - slot.current_bookings} place(s)
                                  </p>
                                </button>
                              );
                            })}
                          </div>
                        )}
                        
                        {hasSurcharge && (
                          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                            <div>
                              <p className="text-sm font-medium text-amber-800">Supplément express</p>
                              <p className="text-xs text-amber-700">+{surchargePercent}% ({surchargePercent === 50 ? "24h" : "48h"})</p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <ManualSlotPicker
                    zoneId={selectedZone}
                    getAuth={getAuth}
                    prefix="delivery"
                    onCreated={(slot, date) => { setDeliveryDate(date); setSelectedDeliverySlot(slot); }}
                  />
                </CardContent>
              </Card>
            )}

            {/* Client Info */}
            {canShowClientForm() && (
              <Card className="fade-in">
                <CardHeader className="pb-3">
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <User className="w-5 h-5 text-primary" />
                    Coordonnées client
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {/* Customer Search */}
                  <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
                    <Label className="flex items-center gap-2 mb-2">
                      <Search className="w-4 h-4" />
                      Rechercher un client existant
                    </Label>
                    <div className="relative">
                      <Input
                        placeholder="Tapez un nom, email ou téléphone..."
                        value={customerSearch}
                        onChange={(e) => setCustomerSearch(e.target.value)}
                        className="pr-10"
                      />
                      <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                      
                      {/* Customer Results Dropdown */}
                      {showCustomerResults && customerResults.length > 0 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-auto">
                          {customerResults.map((customer) => (
                            <button
                              key={customer.id}
                              type="button"
                              onClick={() => selectCustomer(customer)}
                              className="w-full text-left px-4 py-3 hover:bg-slate-50 border-b last:border-b-0 transition-colors"
                            >
                              <p className="font-medium">
                                {customer.first_name} {customer.last_name}
                              </p>
                              <p className="text-sm text-slate-500">
                                {customer.address}
                              </p>
                              <p className="text-xs text-slate-400">
                                {customer.phone && `📞 ${customer.phone}`}
                                {customer.phone && customer.email && " • "}
                                {customer.email && `✉️ ${customer.email}`}
                              </p>
                            </button>
                          ))}
                        </div>
                      )}
                      {showCustomerResults && customerResults.length === 0 && customerSearch.length >= 2 && (
                        <div className="absolute z-50 w-full mt-1 bg-white border rounded-lg shadow-lg p-4 text-center text-slate-500">
                          Aucun client trouvé
                        </div>
                      )}
                    </div>
                    
                    {/* Selected Customer Badge */}
                    {selectedCustomer && (
                      <div className="mt-3 flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center gap-2">
                          <Users className="w-5 h-5 text-green-600" />
                          <span className="font-medium text-green-800">
                            {selectedCustomer.first_name} {selectedCustomer.last_name}
                          </span>
                        </div>
                        <Button 
                          type="button" 
                          variant="ghost" 
                          size="sm" 
                          onClick={clearCustomer}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          Changer
                        </Button>
                      </div>
                    )}
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">Prénom *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleInputChange}
                          placeholder="Prénom"
                          className="mt-1"
                          required
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Nom *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleInputChange}
                          placeholder="Nom"
                          className="mt-1"
                          required
                        />
                      </div>
                    </div>
                    
                    <div>
                      <Label htmlFor="address">Adresse complète *</Label>
                      <Input
                        id="address"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="Numéro, rue, code postal, ville"
                        className="mt-1"
                        required
                      />
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="phone">Téléphone</Label>
                        <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="06 12 34 56 78"
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="email">Email</Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="email@exemple.com"
                          className="mt-1"
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="remarks">Remarques / Articles</Label>
                      <Textarea
                        id="remarks"
                        name="remarks"
                        value={formData.remarks}
                        onChange={handleInputChange}
                        placeholder="Instructions particulières, liste des articles..."
                        className="mt-1"
                        rows={3}
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={loading}
                      className="w-full btn-primary mt-4"
                    >
                      {loading ? "Création en cours..." : "Créer la réservation"}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Summary Sidebar */}
          <div className="lg:col-span-1">
            <Card className="sticky top-6">
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">Récapitulatif</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {selectedZone && (
                  <div className="flex items-center gap-3 pb-3 border-b border-slate-100">
                    <MapPin className="w-5 h-5 text-primary" />
                    <div>
                      <p className="text-xs text-slate-500">Zone</p>
                      <p className="font-medium">
                        {selectedZone === "lyon" ? "Lyon" : selectedZone === "francheville" ? "Francheville" : "Saint-Rambert-d'Albon"}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        {bookingType === "pickup" && "Ramassage seul"}
                        {bookingType === "delivery" && "Livraison seule"}
                        {bookingType === "both" && "Ramassage + Livraison"}
                      </p>
                    </div>
                  </div>
                )}
                
                {selectedPickupSlot && (
                  <div className="flex items-center gap-3 pb-3 border-b border-slate-100 fade-in">
                    <Package className="w-5 h-5 text-pink-500" />
                    <div>
                      <p className="text-xs text-slate-500">Ramassage</p>
                      <p className="font-medium">{format(pickupDate, "EEE d MMM", { locale: fr })}</p>
                      <p className="text-sm text-slate-600">{selectedPickupSlot.start_time} - {selectedPickupSlot.end_time}</p>
                    </div>
                  </div>
                )}
                
                {selectedDeliverySlot && (
                  <div className="flex items-center gap-3 pb-3 border-b border-slate-100 fade-in">
                    <Truck className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="text-xs text-slate-500">Livraison</p>
                      <p className="font-medium">{format(deliveryDate, "EEE d MMM", { locale: fr })}</p>
                      <p className="text-sm text-slate-600">{selectedDeliverySlot.start_time} - {selectedDeliverySlot.end_time}</p>
                    </div>
                  </div>
                )}

                {hasSurcharge && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg fade-in">
                    <p className="text-sm font-medium text-amber-800">Supplément express +{surchargePercent}%</p>
                    <p className="text-xs text-amber-700">{surchargePercent === 50 ? "Délai 24h" : "Délai 48h"}</p>
                  </div>
                )}

                {!selectedZone && (
                  <p className="text-slate-400 text-sm text-center py-2">
                    Sélectionnez une zone pour commencer
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminNewBooking;
